import pixAxios from '@/axios/pixAxios.js';
import countryCodeUtils from '@/services/common/countryCode.utils.js';
import pixUtils from '@/services/common/pix.utils.js';
import Filter from '@/viewModels/Filters/Filter.js';
import mongoTypeConst from '@/consts/mongoType.const.js';

const getLauAutocomplete = ({ searchText, instance, levelCode, parentGeographyCode, countryCode = countryCodeUtils.getMotherCountryCodeByInstance(instance) }) => {
  return pixAxios.get('api/Lau/Autocomplete', {
    params: {
      searchText,
      countryCode,
      levelCode,
      parentGeographyCode,
    },
  });
};

const getClientsAutocomplete = ({ searchText, rows, page }) => {
  return pixAxios.get('api/Client/GetClients', {
    params: {
      ClientCompany: searchText,
      rows,
      page,
    },
  });
};

const getPropertyAutocomplete = ({ searchText, countryCode }) => {
  const filters = [];
  filters.push(new Filter('CountryCode', countryCode, mongoTypeConst.INTEGER, '241', '1'));
  filters.push(new Filter('search', searchText, '', '242', '1'));
  const genericFilters = pixUtils.getFiltersStructure(filters);

  const postData = {
    take: 20,
    skip: 0,
    sortFld: 'RowId',
    sortDir: 'asc',
    filter: genericFilters,
  };

  return pixAxios.post('api/Property/GetPropertyDbProperties', postData);
};

const getLauLabel = ({ GeographyCode, ISOCountryNumber, LevelCode }) => {
  return pixAxios.post('api/Lau/Search', {
    GeographyCode,
    ISOCountryNumber,
    LevelCode,
  });
};

const getMSAAutocomplete = ({ searchText, instance, CountryCode = countryCodeUtils.getMotherCountryCodeByInstance(instance) }) => {
  return pixAxios.post('api/MSA/GetMSAListFromText', {
    GeomLabel: searchText,
    CountryCode,
  });
};

const lauLabel = async ({ config }) => {
  return getLauLabel({ GeographyCode: config.Value }).then((resp) => {
    if (resp.data.length > 0) {
      config.ParamListValues = [{ Name: resp.data[0].GeographyName, Value: config.Value }];
    }
    return resp.data;
  });
};

const lauCountryLabel = async ({ config }) => {
  return getLauLabel({ ISOCountryNumber: config.Value, LevelCode: 'Country' }).then((resp) => {
    if (resp.data.length > 0) {
      config.ParamListValues = [{ Name: resp.data[0].GeographyName, Value: config.Value }];
    }
    return resp.data;
  });
};

const countryTypeahead = async ({ searchText, instance, config }) => {
  return getLauAutocomplete({ searchText, instance, levelCode: 'Country', parentGeographyCode: null }).then((value) => {
    config.ParamListValues = value.data;
    return value.data;
  });
};

const MSATypeahead = async ({ searchText, instance, config }) => {
  return getMSAAutocomplete({ searchText, instance }).then((value) => {
    config.ParamListValues = value.data;
    return value.data;
  });
};

const clientsTypeahead = async ({ searchText, config }) => {
  return getClientsAutocomplete({ searchText, rows: 10, page: 1 }).then((value) => {
    config.ParamListValues = value.data.rows;
    return value.data.rows;
  });
};

const propertyTypeahead = async ({ searchText, countryCode, config }) => {
  return getPropertyAutocomplete({ searchText, countryCode }).then((value) => {
    config.ParamListValues = value.data.rows;
    return value.data.rows;
  });
};

const stateTypeahead = async ({ searchText, instance, config, form }) => {
  const country = getCountryFromForm(form)?.Value;
  if (country) {
    return getLauAutocomplete({ searchText, instance, levelCode: 'State', parentGeographyCode: null, countryCode: country }).then((value) => {
      config.ParamListValues = value.data;
      return value.data;
    });
  } else {
    throw 'First you need to pick a country';
  }
};

const getCountryFromForm = (form) => {
  const country = form.formData.find((formItem) => {
    return formItem.InternalField === 'SubjectInfoCountry';
  });
  return country;
};

const getStateFromForm = (form) => {
  const state = form.formData.find((formItem) => {
    return formItem.InternalField === 'SubjectInfoState';
  });
  return state;
};

const countyTypeahead = async ({ searchText, instance, config, form }) => {
  const state = getStateFromForm(form)?.Value;
  if (state) {
    return getLauAutocomplete({ searchText, instance, levelCode: 'County', parentGeographyCode: state }).then((value) => {
      config.ParamListValues = value.data;
      return value.data;
    });
  } else {
    throw 'First you need to pick a state';
  }
};

const municipalityTypeahead = async ({ searchText, instance, config, form }) => {
  const state = getStateFromForm(form)?.Value;
  if (state) {
    return getLauAutocomplete({ searchText, instance, levelCode: 'Municipality', parentGeographyCode: state }).then((value) => {
      config.ParamListValues = value.data;
      return value.data;
    });
  } else {
    throw 'First you need to pick a state';
  }
};

const clearTypeaheadOptionsIfThereIsNoValue = (countryControl) => {
  if (!countryControl?.Value) {
    countryControl.ParamListValues = [];
  }
};

const clearTypeahead = (control) => {
  if (control) {
    control.Value = null;
    control.ParamListValues = [];
  }
};

const onCountriesChange = (form) => {
  const countryControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_COUNTRIES');
  const stateControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_STATES');
  const countiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_COUNTIES');
  const municipalitiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_MUNICIPALITIES');

  clearTypeaheadOptionsIfThereIsNoValue(countryControl);
  clearTypeahead(stateControl);
  clearTypeahead(countiesControl);
  clearTypeahead(municipalitiesControl);
};

const onStateChange = (form) => {
  const stateControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_STATES');
  const countiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_COUNTIES');
  const municipalitiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_MUNICIPALITIES');

  clearTypeaheadOptionsIfThereIsNoValue(stateControl);
  clearTypeahead(countiesControl);
  clearTypeahead(municipalitiesControl);
};

const onCountyChange = (form) => {
  const countiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_COUNTIES');
  clearTypeaheadOptionsIfThereIsNoValue(countiesControl);
};
const onMunicipalityChange = (form) => {
  const municipalitiesControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_MUNICIPALITIES');
  clearTypeaheadOptionsIfThereIsNoValue(municipalitiesControl);
};

const onClientsChange = (form) => {
  const clientsControl = form.formData.find((control) => control.EmitEventName === 'PROPERTY_CLIENTS');
  clearTypeaheadOptionsIfThereIsNoValue(clientsControl);
};

const autocompletes = {
  PROPERTY_MSA_COUNTRIES: MSATypeahead,
  PROPERTY_COUNTRIES: countryTypeahead,
  PROPERTY_STATES: stateTypeahead,
  PROPERTY_COUNTIES: countyTypeahead,
  PROPERTY_MUNICIPALITIES: municipalityTypeahead,
  PROPERTY_CLIENTS: clientsTypeahead,
  PROPERTY_PROPERTY_LIST: propertyTypeahead,
};

const labels = {
  PROPERTY_COUNTRIES: lauCountryLabel,
  PROPERTY_STATES: lauLabel,
  PROPERTY_COUNTIES: lauLabel,
  PROPERTY_MUNICIPALITIES: lauLabel,
};

const onChanges = {
  PROPERTY_COUNTRIES: onCountriesChange,
  PROPERTY_STATES: onStateChange,
  PROPERTY_COUNTIES: onCountyChange,
  PROPERTY_MUNICIPALITIES: onMunicipalityChange,
  PROPERTY_CLIENTS: onClientsChange,
};

const getAutocomplete = (name) => {
  return autocompletes[name];
};

const getLabel = (name) => {
  return labels[name];
};

const getOnChanges = (name) => {
  return onChanges[name];
};

export default {
  getAutocomplete,
  getLabel,
  getOnChanges,
};

// async searchForProperty(search) {
//   const filters = [];

//   filters.push(new Filter('CountryCode', '840', mongoTypeConst.INTEGER, '241', '1'));
//   filters.push(new Filter('search', search, '', '242', '1'));

//   const genericFilters = pixUtils.getFiltersStructure(filters);

//   const postData = {
//     take: 10,
//     skip: 0,
//     sortFld: 'RowId',
//     sortDir: 'asc',
//     filter: genericFilters,
//   };

//   const {
//     data: { rows },
//   } = await pixAxios.post('api/Property/GetPropertyDbProperties', postData);

//   this.properties = rows;

//   this.properties?.forEach((prop) => {
//     prop.LatLong = `${prop.Latitude} / ${prop.Longitude}`;
//   });
// },
